import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageTransition, FadeIn } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ChevronRight, Plus, Pencil, Trash2, Loader2, Save, GripVertical, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { toAbsoluteUrl } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const optionTypes = [
  { key: 'shapes', label: 'Formes', icon: '🔷' },
  { key: 'lengths', label: 'Longueurs', icon: '📏' },
  { key: 'colors', label: 'Couleurs', icon: '🎨' },
  { key: 'nail_art_options', label: 'Types de Nail Art', icon: '✨' }
];

const emptyOption = { value: '', label: '', price_modifier: 0, image_url: '', description: '' };

export default function AdminPressOnNailsPage() {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('shapes');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [optionForm, setOptionForm] = useState(emptyOption);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetchConfig();
  }, [token]);

  const fetchConfig = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/press-on-nails/config`, { headers });
      setConfig(response.data);
    } catch (err) {
      console.error('Error fetching config:', err);
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!config) return;
    setIsSaving(true);
    try {
      // Ensure "custom" nail art option exists (for "Personnalisé")
      const hasCustomNailArt = config.nail_art_options.some(opt => opt.value === 'custom');
      if (!hasCustomNailArt) {
        config.nail_art_options.push({
          value: 'custom',
          label: 'Personnalisé',
          price_modifier: 0,
          image_url: null,
          description: null
        });
      }

      await axios.put(`${API_URL}/admin/press-on-nails/config`, {
        shapes: config.shapes,
        lengths: config.lengths,
        colors: config.colors,
        nail_art_options: config.nail_art_options,
        base_price: config.base_price,
        photos_required: config.photos_required,
        nail_art_description_required: config.nail_art_description_required
      }, { headers });
      toast.success('Configuration enregistrée');
      setHasChanges(false);
    } catch (err) {
      console.error('Error saving config:', err);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSaving(false);
    }
  };

  const updateOption = (type, index, field, value) => {
    setConfig(prev => {
      const updated = { ...prev };
      updated[type][index][field] = value;
      return updated;
    });
    setHasChanges(true);
  };

  const removeOption = (type, index) => {
    if (!window.confirm('Supprimer cette option ?')) return;
    setConfig(prev => {
      const updated = { ...prev };
      updated[type] = updated[type].filter((_, i) => i !== index);
      return updated;
    });
    setHasChanges(true);
  };

  const openAddDialog = (type) => {
    setActiveTab(type);
    setEditingOption(null);
    setOptionForm({ ...emptyOption });
    setIsAddDialogOpen(true);
  };

  const openEditDialog = (type, index) => {
    const option = config?.[type]?.[index];
    if (!option) return;
    setActiveTab(type);
    setEditingOption({ type, index });
    setOptionForm({
      value: option.value || '',
      label: option.label || '',
      price_modifier: option.price_modifier ?? 0,
      image_url: option.image_url || '',
      description: option.description || ''
    });
    setIsAddDialogOpen(true);
  };

  const handleImageUpload = async (file) => {
    const payload = new FormData();
    payload.append('file', file);
    setIsUploadingImage(true);
    try {
      const response = await axios.post(`${API_URL}/uploads/service-image`, payload);
      const uploadedUrl = response.data?.image_url;
      if (uploadedUrl) {
        setOptionForm(prev => ({ ...prev, image_url: uploadedUrl }));
        toast.success('Image uploadée');
      } else {
        toast.error('Réponse upload invalide');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      toast.error('Erreur lors de l\'upload de l\'image');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const saveOption = () => {
    if (!optionForm.value.trim() || !optionForm.label.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    const optionValue = editingOption
      ? optionForm.value.trim()
      : optionForm.value.toLowerCase().trim().replace(/\s+/g, '_');
    const nextOption = {
      value: optionValue,
      label: optionForm.label.trim(),
      price_modifier: parseFloat(optionForm.price_modifier) || 0
    };
    if (activeTab === 'colors' || activeTab === 'nail_art_options') {
      nextOption.image_url = optionForm.image_url?.trim() || null;
    }
    if (activeTab === 'nail_art_options') {
      nextOption.description = optionForm.description?.trim() || null;
    }
    setConfig(prev => {
      const updated = { ...prev };
      if (editingOption) {
        updated[activeTab] = updated[activeTab].map((item, idx) =>
          idx === editingOption.index ? nextOption : item
        );
      } else {
        updated[activeTab].push(nextOption);
      }
      return updated;
    });
    setHasChanges(true);
    setIsAddDialogOpen(false);
    setEditingOption(null);
    toast.success(editingOption ? 'Option modifiée' : 'Option ajoutée');
  };

  if (isLoading) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 size={32} className="animate-spin text-[var(--gold)]" />
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-8 px-6 bg-[var(--charcoal)]" data-testid="admin-pon-hero">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-4 text-white"
          >
            Press On Nails - Configuration
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/60"
          >
            Gérez les options du formulaire de commande
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)]">Accueil</Link>
            <ChevronRight size={14} />
            <Link to="/admin" className="hover:text-[var(--gold)]">Admin</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Press On Nails</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 px-6" data-testid="admin-pon-content">
        <div className="max-w-[1200px] mx-auto">
          {/* Save Button */}
          <div className="flex justify-between items-center mb-8">
            <h2 className="font-['Cormorant_Garamond'] text-2xl text-[var(--charcoal)]">
              Options du formulaire
            </h2>
            <Button
              onClick={saveConfig}
              disabled={isSaving || !hasChanges}
              className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none disabled:opacity-50"
              data-testid="btn-save-config"
            >
              {isSaving ? (
                <><Loader2 size={16} className="animate-spin mr-2" /> Enregistrement...</>
              ) : (
                <><Save size={16} className="mr-2" /> Enregistrer{hasChanges && ' *'}</>
              )}
            </Button>
          </div>

          {hasChanges && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 flex items-center gap-3 text-amber-700">
              <AlertCircle size={20} />
              <span className="text-sm">Modifications non enregistrées</span>
            </div>
          )}

          {/* Base Settings */}
          <FadeIn>
            <div className="bg-white p-6 border border-[var(--champagne)] mb-8">
              <h3 className="font-medium text-[var(--charcoal)] mb-4">Paramètres généraux</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-4 border border-[var(--champagne)] bg-[var(--champagne)]/10">
                  <Label className="text-[var(--charcoal)] font-medium">Prix de base du coffret (€)</Label>
                  <p className="text-xs text-[var(--charcoal)]/55 mt-1 mb-3">
                    Prix affiche au client avant ajout des supplements de forme, longueur, couleur ou nail art.
                  </p>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={config?.base_price || 0}
                    onChange={(e) => {
                      setConfig(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }));
                      setHasChanges(true);
                    }}
                    data-testid="input-base-price"
                  />
                  <div className="mt-3 flex items-end gap-2">
                    <span className="font-['Cormorant_Garamond'] text-3xl text-[var(--charcoal)]">
                      {config?.base_price || 0}€
                    </span>
                    <span className="text-xs text-[var(--charcoal)]/55 pb-1">
                      prix de depart affiche
                    </span>
                  </div>
                </div>
                <div>
                  <Label>Photos requises</Label>
                  <Input
                    type="number"
                    min="0"
                    max="5"
                    value={config?.photos_required || 2}
                    onChange={(e) => {
                      setConfig(prev => ({ ...prev, photos_required: parseInt(e.target.value) || 2 }));
                      setHasChanges(true);
                    }}
                    data-testid="input-photos-required"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={config?.nail_art_description_required || false}
                    onCheckedChange={(checked) => {
                      setConfig(prev => ({ ...prev, nail_art_description_required: checked }));
                      setHasChanges(true);
                    }}
                    data-testid="switch-description-required"
                  />
                  <Label className="cursor-pointer">Description nail art obligatoire (si personnalisé)</Label>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Option Tabs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {optionTypes.map((optType) => (
              <FadeIn key={optType.key}>
                <div className="bg-white border border-[var(--champagne)]">
                  <div className="p-4 border-b border-[var(--champagne)] flex justify-between items-center">
                    <h3 className="font-medium text-[var(--charcoal)] flex items-center gap-2">
                      <span>{optType.icon}</span>
                      {optType.label}
                      <span className="text-[var(--charcoal)]/40 text-sm">({config?.[optType.key]?.length || 0})</span>
                    </h3>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openAddDialog(optType.key)}
                      className="rounded-none"
                      data-testid={`btn-add-${optType.key}`}
                    >
                      <Plus size={14} className="mr-1" /> Ajouter
                    </Button>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    {config?.[optType.key]?.map((opt, index) => (
                      <div
                        key={index}
                        className="p-3 border-b border-[var(--champagne)]/50 last:border-b-0 flex items-center gap-3 hover:bg-[var(--champagne)]/10"
                        data-testid={`option-${optType.key}-${index}`}
                      >
                        <GripVertical size={14} className="text-[var(--charcoal)]/20 cursor-grab" />
                        {(optType.key === 'colors' || optType.key === 'nail_art_options') && opt.image_url && (
                          <div className="w-10 h-10 rounded-md overflow-hidden border border-[var(--champagne)] bg-white shrink-0">
                            <img
                              src={toAbsoluteUrl(opt.image_url)}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        {optType.key === 'colors' && opt.value !== 'custom' && (
                          <div
                            className="w-6 h-6 rounded-full border border-gray-200"
                            style={{ backgroundColor: opt.value }}
                          />
                        )}
                        <div className="flex-1">
                          <div className="text-sm font-medium text-[var(--charcoal)]">{opt.label}</div>
                          {optType.key === 'nail_art_options' && opt.description && (
                            <div className="text-xs text-[var(--charcoal)]/60 mt-1">
                              {opt.description}
                            </div>
                          )}
                          <div className="text-xs text-[var(--charcoal)]/50">{opt.value}</div>
                        </div>
                        <div className="text-sm text-[var(--gold)]">
                          {opt.price_modifier > 0 ? `+${opt.price_modifier}€` : '—'}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(optType.key, index)}
                          className="text-[var(--charcoal)]/60 hover:text-[var(--charcoal)] h-8 w-8 p-0"
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeOption(optType.key, index)}
                          className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ))}
                    {(!config?.[optType.key] || config[optType.key].length === 0) && (
                      <div className="p-8 text-center text-[var(--charcoal)]/40 text-sm">
                        Aucune option
                      </div>
                    )}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Important Note */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 text-blue-700 text-sm">
            <strong>Note :</strong> Le type de Nail Art &quot;Personnalisé&quot; (value: &quot;custom&quot;) est automatiquement
            ajouté à l&apos;enregistrement si absent. Lorsqu&apos;un client choisit &quot;Personnalisé&quot;, un commentaire
            descriptif sera obligatoire.
          </div>
        </div>
      </section>

      {/* Add Option Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-xl">
              Ajouter une option
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Libellé affiché *</Label>
              <Input
                value={optionForm.label}
                onChange={(e) => setOptionForm(prev => ({ ...prev, label: e.target.value }))}
                placeholder="Ex: Stiletto, Long, Rose Gold..."
                data-testid="input-option-label"
              />
            </div>
            <div>
              <Label>Valeur technique *</Label>
              <Input
                value={optionForm.value}
                onChange={(e) => setOptionForm(prev => ({ ...prev, value: e.target.value }))}
                placeholder={activeTab === 'colors' ? '#FF5733 ou nom' : 'ex: stiletto, long...'}
                data-testid="input-option-value"
              />
              <p className="text-xs text-[var(--charcoal)]/50 mt-1">
                {activeTab === 'colors' ? 'Code couleur HEX ou nom' : 'Identifiant unique (sans espaces)'}
              </p>
            </div>
            <div>
              <Label>Supplément prix (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={optionForm.price_modifier}
                onChange={(e) => setOptionForm(prev => ({ ...prev, price_modifier: e.target.value }))}
                placeholder="0"
                data-testid="input-option-price"
              />
            </div>
            {(activeTab === 'colors' || activeTab === 'nail_art_options') && (
              <div>
                <Label>Image (URL ou upload)</Label>
                <Input
                  value={optionForm.image_url}
                  onChange={(e) => setOptionForm(prev => ({ ...prev, image_url: e.target.value }))}
                  placeholder="https://..."
                  data-testid="input-option-image"
                />
                <div className="mt-2 flex items-center gap-3">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleImageUpload(file);
                      }
                    }}
                    disabled={isUploadingImage}
                    data-testid="input-option-image-file"
                  />
                  {optionForm.image_url && (
                    <div className="w-12 h-12 rounded-md overflow-hidden border border-[var(--champagne)]">
                      <img
                        src={toAbsoluteUrl(optionForm.image_url)}
                        alt="Aperçu"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === 'nail_art_options' && (
              <div>
                <Label>Description</Label>
                <Textarea
                  value={optionForm.description}
                  onChange={(e) => setOptionForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Ex: Décoration sur 2 ongles, design complexe..."
                  className="min-h-[100px]"
                  data-testid="input-option-description"
                />
              </div>
            )}
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)} className="rounded-none">
              Annuler
            </Button>
            <Button
              onClick={saveOption}
              className="bg-[var(--gold)] text-white hover:bg-[var(--dark-gold)] rounded-none"
              data-testid="btn-confirm-add-option"
            >
              {editingOption ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageTransition>
  );
}
